<template>
  <div>
    <button
        v-if="designerStore.helpArticle"
        type="button"
        aria-label="Open help"
        class="
          tw-fixed tw-top-20 tw-right-0
          tw-text-white /* Appearance */
          tw-p-3 tw-shadow-lg
          hover:tw-bg-blue-700
          focus:tw-outline-none focus:tw-ring-2
          focus:tw-ring-blue-500 focus:tw-ring-offset-2
          tw-transition-colors
          md:tw-hidden
        "
        :style="{
          backgroundColor: designerStore.branding.branding_colour,
        }"
        style="border-radius: 5px 0 0 5px; z-index: 500; /* Custom radius and z-index */"
        @click="openHelp"
    >
      <svg
          xmlns="http://www.w3.org/2000/svg"
          class="tw-h-6 tw-w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
      >
        <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M8.228 9.00005C8.39762 8.03251 9.14083 7.28357 10.1098 7.10546C11.0788 6.92736 12.0878 7.10103 12.8718 7.58891C13.6557 8.07678 14.1621 8.84276 14.2819 9.70788C14.3999 10.5618 14.1225 11.4237 13.5342 12.079C13.2622 12.388 12.9016 12.7314 12.606 13.007C12.003 13.568 11.798 14.171 11.798 14.814V15.2"
        />
        <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 18.2C12.3866 18.2 12.7 17.8866 12.7 17.5C12.7 17.1134 12.3866 16.8 12 16.8C11.6134 16.8 11.3 17.1134 11.3 17.5C11.3 17.8866 11.6134 18.2 12 18.2Z"
        />
        <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z"
        />
      </svg>
    </button>

    <transition name="fade">
      <div
          v-if="isHelpOpen"
          class="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50"
          style="z-index: 1050;"
          aria-hidden="true"
          @click="closeHelp"
      />
    </transition>

    <div
        v-show="isPanelVisible"
        ref="helpPanel"
        :class="[
          'tw-fixed tw-top-0 tw-right-0 tw-h-screen tw-w-4/5',
          'tw-bg-white tw-shadow-xl tw-p-5 md:tw-p-6',
          'tw-flex tw-flex-col',
          'tw-transition-transform tw-duration-300 tw-ease-in-out',
          isHelpOpen ? 'tw-translate-x-0' : 'tw-translate-x-full'
        ]"
        style="z-index: 1100; max-width: 100vw;"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-panel-title"
    >
      <div class="tw-flex tw-justify-end tw-items-center tw-mb-4 tw-flex-shrink-0">
        <button
            type="button"
            aria-label="Close help"
            class="tw-p-1 tw-rounded-md tw-text-gray-500 hover:tw-bg-gray-100 hover:tw-text-gray-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-inset focus:tw-ring-gray-400"
            @click="closeHelp"
        >
          <svg
              xmlns="http://www.w3.org/2000/svg"
              class="tw-h-6 tw-w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
          >
            <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div
          class="tw-flex-grow tw-overflow-y-auto"
          v-html="designerStore.helpArticle"
      />
    </div>
  </div>
</template>

<script>
import {useDesignerStore} from "@/stores/DesignerStore"; // Assuming Pinia store path

export default {
  name: 'HelpSlideout',
  setup() {
    const designerStore = useDesignerStore();
    // Make sure helpArticle exists in the store, otherwise v-html might try to render undefined
    // You might want default content or ensure it's always at least an empty string
    return {designerStore};
  },
  data() {
    return {
      isHelpOpen: false,
      isPanelVisible: false, // Used to keep the panel in DOM during closing transition
      originalBodyOverflow: '',
    };
  },
  watch: {
    isHelpOpen(isOpen) {
      if (isOpen) {
        // When opening
        this.isPanelVisible = true; // Make panel visible before animation starts
        this.originalBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden'; // Prevent background scrolling
        // Focus management: Focus the close button after panel opens
        this.$nextTick(() => {
          // Find the close button within the panel ref
          const closeButton = this.$refs.helpPanel?.querySelector('button[aria-label="Close help"]');
          closeButton?.focus();
        });

      } else {
        // When closing
        document.body.style.overflow = this.originalBodyOverflow; // Restore background scrolling
        // Use timeout matching transition duration to remove panel from DOM after animation
        setTimeout(() => {
          if (!this.isHelpOpen) { // Check again in case it was reopened quickly
            this.isPanelVisible = false;
          }
        }, 300); // Match the duration-300 class (300ms)
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleEscapeKey);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEscapeKey);
    // Ensure body scroll is restored if component is unmounted while open
    if (this.isHelpOpen) {
      document.body.style.overflow = this.originalBodyOverflow;
    }
  },
  methods: {
    openHelp() {
      this.isHelpOpen = true;
    },
    closeHelp() {
      this.isHelpOpen = false;
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.isHelpOpen) {
        this.closeHelp();
      }
    }
  }
};
</script>

<style scoped>
/* Fade transition for the overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease; /* duration-300 */
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>